export interface Pricing {
  id: string;
  partnerId: string;
  countryId: string;
  currency: string;
  notes: string;
}

export enum DiscountType {
  General = 'general',
  Individual = 'individual',
}

export enum ValueType {
  Percentage = 'percentage',
  FixedValue = 'fixed_value',
}

export enum DiscountPriceType {
  WSP = 'WSP',
  EUP = 'EUP',
}

export enum DiscountStatus {
  Approved = 'approved',
  Pending = 'pending',
}

export enum PriceRequestStatus {
  Approved = 'approved',
  Pending = 'pending',
}

export interface Discount {
  id: string;
  partnerId?: string;
  discountType: DiscountType;
  priceType: DiscountPriceType;
  bundleIds?: string[];
  serviceDataReferenceKey?: string;
  discountValue: number;
  valueType: ValueType;
  validFrom: Date;
  validTo: Date;
  reason: string;
  createdBy?: string;
  updatedAt?: Date;
  createdAt?: Date;
  active: boolean;
  partner?: PricingPartner;
  lastStatus?: string;
  pivot?: DiscountPivot;
}

export interface DiscountPivot {
  serviceDataId: string;
  discountId: string;
  amount: number | null;
}

export interface DiscountPivotApiModelData {
  service_data_id: string;
  discount_id: string;
  amount?: string | number | null;
}

export interface CreateDiscountPayload extends Discount {
  partnerIds?: string[];
}

export interface DiscountList {
  data: Discount[];
  totalCount: number;
  hasMorePages: boolean;
  perPage: number;
  currentPage: number;
}

export interface PriceRequest {
  id: string;
  partnerId: string;
  bundleIds: string[];
  price?: number;
  validFrom: Date;
  createdBy?: string;
  createdAt?: Date;
  partner?: PricingPartner;
  currency?: string;
  status?: PriceRequestStatus | string;
}

export interface PriceRequestList {
  data: PriceRequest[];
  totalCount: number;
  hasMorePages: boolean;
  perPage: number;
  currentPage: number;
}

export interface DiscountApiModelData {
  id?: string;
  partner_id?: string;
  partner_ids?: string[];
  discount_type: string;
  price_type?: string;
  bundle_ids: string[];
  reference_key?: string;
  discount_value: string;
  discount_value_type: string;
  notes: string;
  from_date: string;
  to_date: string;
  active: boolean;
  partner?: PricingPartnerApiModelData;
  created_by?: string;
  updated_at?: string;
  created_at?: string;
  last_status?: string;
  pivot?: DiscountPivotApiModelData;
}

export interface DiscountApiModel {
  data: DiscountApiModelData[];
  total_count: number;
  has_more_pages: boolean;
  per_page: number;
  current_page: number;
}

export interface PricingPartner {
  id: string;
  name: string;
  accountManagerId?: string;
  country: string;
  email: string;
  phone: string;
  currency?: string;
  notes?: string;
  missingPrices?: number;
  pendingPriceRequests?: number;
  active: boolean;
  discountedPrice?: number;
  pricelist?: BundlePrice[];
  priceRequests?: BundlePrice[];
  discounts?: Discount[];
  paymentTypes?: string[];
}

export interface PricingPartnerList {
  data: PricingPartner[];
  totalCount: number;
  hasMorePages: boolean;
  perPage: number;
  currentPage: number;
}

export interface BundlePricePivot {
  partnerId: string;
  generatedBy?: string;
  bundleId: string;
  id: string;
  price?: number;
  validFrom: Date;
}
export interface BundlePrice {
  id: string;
  name: string;
  internalName: string;
  discountedPrice?: number;
  discount?: Discount;
  pivot: BundlePricePivot;
  isPriceRequest?: boolean;
  countryEupPrice?: number;
  countryEupDiscountedPrice?: number;
  countryEupValidFrom?: Date;
}

export interface PricingPartnerApiModelData {
  id: string;
  name: string;
  account_manager_id?: string;
  country: string;
  email: string;
  phone: string;
  currency?: string;
  notes?: string;
  active: boolean;
  missing_prices?: number;
  pending_price_requests?: number;
  pricelist?: BundlePriceApiModelData[];
  price_requests?: PriceRequestApiModelData[];
  payment_types?: string[];
}

export interface BundlePricePivotApiModelData {
  partner_id: string;
  bundle_id: string;
  generated_by?: string;
  id: string;
  price?: number;
  valid_from: Date;
}

export interface BundlePriceApiModelData {
  id: string;
  name: string;
  internal_name: string;
  discounted_price?: number;
  discount?: DiscountApiModelData;
  pivot: BundlePricePivotApiModelData;
  country_eup_price?: string | null;
  country_eup_discounted_price?: string | number | null;
  country_eup_valid_from?: string | null;
}

export interface PriceRequestApiModelData {
  id: string;
  name?: string;
  internal_name?: string;
  discounted_price?: number;
  discount?: DiscountApiModelData;
  partner_id: string;
  bundle_id: string;
  bundle_ids?: string[];
  price?: number;
  valid_from: Date;
  created_by?: string;
  created_at?: string;
  partner?: PricingPartnerApiModelData;
  currency?: string;
  status?: string;
  last_status?: string;
  bundle?: {
    id?: string;
    name?: string;
    internal_name?: string;
  };
}

export interface PriceRequestApiModel {
  data: PriceRequestApiModelData[];
  total_count: number;
  has_more_pages: boolean;
  per_page: number;
  current_page: number;
}

export interface PricingPartnerApiModel {
  data: PricingPartnerApiModelData[];
  total_count: number;
  has_more_pages: boolean;
  per_page: number;
  current_page: number;
}

export interface PartnerBundle {
  id: string;
  name: string;
}

export enum PricingExportType {
  Billing = 'billing',
  Canceled = 'canceled',
}

export enum CountryPriceType {
  EUP = 'EUP',
  WSP = 'WSP',
}

export interface PricingCountry {
  id: string;
  name: string;
  currency?: string;
  notes?: string;
  pricelist?: CountryBundlePrice[];
}

export interface PricingCountryList {
  data: PricingCountry[];
  totalCount: number;
  hasMorePages: boolean;
  perPage: number;
  currentPage: number;
}

export interface CountryBundlePricePivot {
  id: string;
  countryId: string;
  bundleId: string;
  price?: number;
  type: CountryPriceType;
  validFrom: Date;
  generatedBy?: string;
}

export interface CountryBundlePrice {
  id: string;
  name: string;
  internalName: string;
  pivot: CountryBundlePricePivot;
}

export interface PricingCountryApiModelData {
  id: string;
  name: string;
  currency?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  pricelist?: CountryBundlePriceApiModelData[];
}

export interface PricingCountryApiModel {
  data: PricingCountryApiModelData[];
  total_count: number;
  has_more_pages: boolean;
  per_page: number;
  current_page: number;
}

export interface CountryBundlePricePivotApiModelData {
  id: string;
  country_id: string;
  bundle_id: string;
  price?: number | string;
  type: CountryPriceType;
  valid_from: string;
  generated_by?: string;
}

export interface CountryBundlePriceApiModelData {
  id: string;
  name: string;
  internal_name?: string;
  pivot: CountryBundlePricePivotApiModelData;
}

/** One country details row per bundle, holding its current WSP and EUP. */
export interface CountryBundleRow {
  id: string;
  name: string;
  wsp: CountryBundlePrice | null;
  eup: CountryBundlePrice | null;
}

export interface PricingServiceData {
  id: string;
  referenceKey: string;
  partnerId: string;
  wholesalePrice: number | null;
  discountedPrice: number | null;
  eupPrice: number | null;
  discountedEupPrice: number | null;
  bundles: PartnerBundle[];
  discounts: Discount[];
}

export interface PricingServiceDataApiModelData {
  id: string;
  reference_key: string;
  partner_id: string;
  wholesale_price?: string | number | null;
  discounted_price?: string | number | null;
  eup_price?: string | number | null;
  discounted_eup_price?: string | number | null;
  bundles?: { id: string; name?: string; internal_name?: string }[];
  discounts?: DiscountApiModelData[];
}

export interface PricingServiceDataApiModel {
  data: PricingServiceDataApiModelData[];
  total_count?: number;
  has_more_pages?: boolean;
  per_page?: number;
  current_page?: number;
}
